<template>
  <Block2Cols>
    <template #col1>
      <MARSTitle><span class="whitespace-nowrap">{{ $t('platform.title_1') }}</span><br /> {{ $t('platform.title_2') }}</MARSTitle>
      <p>
        <NuxtLink :to="$t('menuInfo.viewer-3d-ar.path').toString()" :title="$t('menuInfo.viewer-3d-ar.title').toString()" class="underline">{{ $t('platform.previous_page') }}</NuxtLink>         
        <span v-html="$t('platform.viewer_description')" />
      </p>
      <p class="mt-4">{{ $t('platform.overall_picture') }}</p>
    </template>
    <template #col2>
      <img loading="lazy" style="height:130px" class="block" src="~/assets/img/logo.svg" alt="{{ $t('platform.image_alt.logo') }}">
    </template>
   </Block2Cols>
   <BlockBand class="mt-16" bandColor="#F2F2F2">
    <Block1Col compact no-padding>
      <img loading="lazy" :src="localizedImage" alt="{{ $t('platform.image_alt.overall_workflow') }}">
    </Block1Col>
   </BlockBand>

   <Block2Cols>
    <template #col1>
      <p>{{ $t('platform.user_experience_analysis') }}</p>
      <p v-html="$t('platform.distribution_description')" />
    </template>
    <template #col2>
      <p v-html="$t('platform.control_panel_description')" />
      <p>{{ $t('platform.make_it_real') }}</p>
    </template>
   </Block2Cols>

   <Block2Cols>
    <template #col1>
      <MARSTitle>{{ $t('platform.lets_start') }}</MARSTitle>
      <p>{{ $t('platform.workflow_follow') }}</p>
    </template>
    <template #col2>
       <source type="image/webp" srcset="~/assets/img/landing/Generic_workflow.webp">
       <source type="image/png" srcset="~/assets/img/landing/Generic_workflow.png">
       <img loading="lazy" src="~/assets/img/landing/Generic_workflow.png" alt="{{ $t('platform.image_alt.generic_workflow') }}">
    </template>
   </Block2Cols>
   <Block2Cols compact class="mt-8">
    <template #col1>
      <p>{{ $t('platform.review_process') }}</p>
      <h3>{{ $t('platform.create_model') }}</h3>
      <p>
       {{ $t('platform.design_product') }}
      </p>
    </template>
    <template #col2>
      <picture>
       <source type="image/webp" srcset="~/assets/img/landing/Keyshot_step0.webp">
       <source type="image/jpeg" srcset="~/assets/img/landing/Keyshot_step0.jpg">
       <img loading="lazy" src="~/assets/img/landing/Keyshot_step0.jpg" alt="{{ $t('platform.image_alt.keyshot_step_0') }}">
      </picture>
    </template>
   </Block2Cols>
   <Block2Cols compact class="mt-8">
    <template #col1>
      <h2>{{ $t('platform.step_1') }}</h2>
      <h3>{{ $t('platform.export_glb') }}</h3>
      <p>{{ $t('platform.export_glb_description') }}</p>
      <p>{{ $t('platform.glb_note') }}</p>
    </template>
    <template #col2>
      <picture>
       <source type="image/webp" srcset="~/assets/img/landing/Keyshot_step1.webp">
       <source type="image/jpeg" srcset="~/assets/img/landing/Keyshot_step1.jpg">
       <img loading="lazy" src="~/assets/img/landing/Keyshot_step1.jpg" alt="{{ $t('platform.image_alt.keyshot_step_1') }}">
      </picture>
    </template>
   </Block2Cols>
   <Block2Cols compact class="mt-8">
    <template #col1>
      <h2>{{ $t('platform.step_2') }}</h2>
      <h3>{{ $t('platform.import_glb') }}</h3>
      <p v-html="$t('platform.import_glb_description')" />
    </template>
    <template #col2>
      <picture>
       <source type="image/webp" srcset="~/assets/img/landing/Keyshot_step2.webp">
       <source type="image/jpeg" srcset="~/assets/img/landing/Keyshot_step2.jpg">
       <img loading="lazy" src="~/assets/img/landing/Keyshot_step2.jpg" alt="{{ $t('platform.image_alt.keyshot_step_2') }}">
      </picture>
    </template>
   </Block2Cols>
   <Block2Cols compact class="mt-8">
    <template #col1>
      <h2>{{ $t('platform.step_3') }}</h2>
      <h3>{{ $t('platform.share_it') }}</h3>
      <p>{{ $t('platform.share_it_description') }}</p>
      <p>{{ $t('platform.viewer_capabilities') }}</p>
      <ul>
       <li v-html="$t('platform.direct_link')" />
       <li v-html="$t('platform.embed_code')" />
       <li v-html="$t('platform.qr_code')" />
      </ul>
    </template>
    <template #col2>
      <picture>
       <source type="image/webp" srcset="~/assets/img/landing/Keyshot_step3.webp">
       <source type="image/jpeg" srcset="~/assets/img/landing/Keyshot_step3.jpg">
       <img loading="lazy" src="~/assets/img/landing/Keyshot_step3.jpg" alt="{{ $t('platform.image_alt.keyshot_step_3') }}">
      </picture>
    </template>
   </Block2Cols>

   <Block2Cols>
    <template #col1>
      <h2>{{ $t('platform.see_results') }}</h2>
      <p>{{ $t('platform.universal_viewer') }}</p>
    </template>
    <template #col2>
      <div class="w-full h-iframeContainer md:h-iframeContainerMd lg:mt-0 viewer3d" style="min-height: 400px; border: 1px solid lightgray;">
       <iframe loading="lazy"
            allow="xr-spatial-tracking *;fullscreen *; web-share *" 
            allowfullscreen allowtransparency="true"
            title="My AR Studio Universal Viewer: example of embedding into a web page."
            class="relative"
            style="width:100%; height:100%;"
            :src="'https://cdn.myarstudio.cloud/f2f01834-6984-4e4d-bd20-8e7586523cf0/Playground/Products/86d6b5d1-ed63-5a2d-8018-f214d5c3fc41/default/fe44c9a6-e0be-52e7-89bb-9320010337fe/index.html?language=' + $i18n.locale"
       />
      </div>
    </template>
   </Block2Cols>
   <Block2Cols compact class="mt-8">
    <template #col1>
      <p v-html="$t('platform.copied_code')" />
      <p v-html="$t('platform.we_use_it')" />
    </template>
    <template #col2>
      <div class="w-full overflow-hidden overflow-x-scroll bg-lightestgray p-4" style="border-radius: 20px;">
       <iframeCodeHardcoded />
      </div>
    </template>
   </Block2Cols>

   <Block1Col>
    <p class="pb-4">{{ $t('platform.sharing_possibilities') }}</p>
    <div>
      <picture>
       <source type="image/webp" srcset="~/assets/img/universal-viewer-flow.webp">
       <source type="image/jpeg" srcset="~/assets/img/universal-viewer-flow.jpg">
       <img loading="lazy" src="~/assets/img/universal-viewer-flow.jpg" alt="{{ $t('platform.image_alt.universal_viewer_flow') }}">
      </picture>
    </div>
   </Block1Col>

   <Block2Cols>
    <template #col1>
      <p v-html="$t('platform.prototyping')" />
      <p v-html="$t('platform.publish_on_website')" />
    </template>
    <template #col2>
      <p v-html="$t('platform.no_coding_skills')" />
      <ActionButton :href="panelLinks.registerUrl.value" :value="$t('home.free-trial').toString()" :bg="Colors.orange" :fg="Colors.white" />
    </template>
   </Block2Cols>

   <Block1Col class="mb-8">
    <MARSTitle>{{ $t('platform.some_examples') }}</MARSTitle>
    <p>{{ $t('platform.examples_intro') }}</p>
   </Block1Col>

   <LandingShowcaseSelector />

   <Block2Cols>
    <template #col1>
      <MARSTitle>{{ $t('platform.common_distribution') }}</MARSTitle>
      <p v-html="$t('platform.mass_distribution')" />
    </template>
    <template #col2>
      <p v-html="$t('platform.physical_store')" />
      <p v-html="$t('platform.rapid_prototyping')" />
      <p v-html="$t('platform.indie_maker')" />
    </template>
   </Block2Cols>

   <Block2Cols>
    <template #col1>
      <MARSTitle><div v-html="$t('platform.ar_revolution')" /></MARSTitle>
      <p>{{ $t('platform.ar_love') }}</p>
    </template>
    <template #col2>
      <p v-html="$t('platform.ar_real')" />
      <p>{{ $t('platform.ar_advantages') }}</p>
      <p>{{ $t('platform.ar_customers') }}</p>
      <p v-html="$t('platform.no_app')" />
    </template>
   </Block2Cols>

   <Block2Cols>
    <template #col1>
      <MARSTitle>{{ $t('platform.which_real') }}</MARSTitle>
      <p>{{ $t('platform.play_game') }}</p>
      <p>{{ $t('platform.which_chair') }}</p>
    </template>
    <template #col2>
      <picture>
       <source type="image/webp" srcset="~/assets/img/landing/WhichOneIsReal.webp">
       <source type="image/jpeg" srcset="~/assets/img/landing/WhichOneIsReal.jpg">
       <img loading="lazy" src="~/assets/img/landing/WhichOneIsReal.jpg" alt="{{ $t('platform.image_alt.which_one_is_real') }}">
      </picture>
    </template>
   </Block2Cols>
   <Block2Cols items-center compact class="mt-10">
    <template #col1>
      <p>{{ $t('platform.lets_discover') }}</p>
    </template>
    <template #col2>
      <div style="position: relative; width: 100%; padding-top: 56.22%;">
       <div class="w-full h-full absolute top-0">
        <iframe v-if="youtubeEnabled" loading="lazy" title="YouTube: Reverse case history" width="100%" height="100%" src="https://www.youtube.com/embed/zoYL91VlvGo?rel=0&start=61" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen />
        <iframe v-else loading="lazy" title="YouTube: Reverse case history" width="100%" height="100%" src="https://www.youtube-nocookie.com/embed/zoYL91VlvGo?rel=0&start=61" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen />
       </div>
      </div>
    </template>
   </Block2Cols>

   <div class="w-full image-discover bg-visualizer-small md:bg-visualizer-medium laptop:bg-visualizer bg-center bg-cover mt-30">
    <div class="container h-full mx-auto">
      <div class="flex flex-col justify-around h-full p-5 laptop:ml-24 md:ml-8">
       <div class="xl:w-9/12 w-12/12">
        <MARSTitle text-color="text-white" class="text-xl md:text-2xl laptop:text-45xl md:w-3/4">
          <div v-html="$t('platform.sell_more')" />
        </MARSTitle>
        <p class="text-white text-xl" v-html="$t('platform.sell_more_subtitle')"></p>
        <ActionButton :href="panelLinks.registerUrl.value" :value="$t('home.free-trial').toString()" :bg="Colors.orange" :fg="Colors.white" />
       </div>
      </div>
    </div>
   </div>
</template>

<script lang="ts" setup>

import MyARStudio_Overall_Workflow_en from '~/assets/img/landing/MyARStudio_Overall_Workflow_en.webp'
import MyARStudio_Overall_Workflow_it from '~/assets/img/landing/MyARStudio_Overall_Workflow_it.webp'
import MyARStudio_Overall_Workflow_de from '~/assets/img/landing/MyARStudio_Overall_Workflow_de.webp'

import { useIubenda } from 'mars-iubenda-vite';
const {youtubeEnabled} = useIubenda()
const panelLinks = usePanelLinks()

const { locale } = useI18n()

const localizedImage = computed(() => {
  const images : {[key:string]: any} = {
    en: MyARStudio_Overall_Workflow_en,
    it: MyARStudio_Overall_Workflow_it,
    de: MyARStudio_Overall_Workflow_de,
  }
  return images[locale.value] || images.en
})

</script>

<style scoped>
h3 {
  @apply text-3xl text-highlight font-bold mt-2
}
</style>
